import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Trash2, GripVertical, Pencil, X } from "lucide-react";
import { api } from "../../api";
import { useApp } from "../../store";
import { MEDIA_SECTIONS } from "../../sections";
import { MediaField, MEDIA_FIELDS } from "./MediaField";

const inputCls =
  "w-full bg-white/[0.05] border border-white/15 focus:border-[var(--accent)] focus:outline-none px-3 py-2.5 text-sm transition-colors duration-300";

export const GalleryManager = () => {
  const { t, lang } = useApp();
  const [items, setItems] = useState(null);
  const [captionFr, setCaptionFr] = useState("");
  const [captionEn, setCaptionEn] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/gallery").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const addFile = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      const abs = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      await api.post("/admin/gallery", { url: abs, caption_fr: captionFr, caption_en: captionEn, section });
      setCaptionFr("");
      setCaptionEn("");
      toast.success(t("admin_saved"));
      load();
    } catch {
      toast.error(t("admin_save_error"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    await api.delete(`/admin/gallery/${item.id}`);
    load();
  };

  const [section, setSection] = useState("realisations");
  const [dragIndex, setDragIndex] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ caption_fr: "", caption_en: "", section: "realisations" });

  const persistOrder = async (ordered) => {
    try {
      await api.patch("/admin/gallery/reorder", { ids: ordered.map((x) => x.id) });
      toast.success(t("admin_order_saved"));
    } catch {
      toast.error(t("admin_save_error"));
    }
  };

  const onDropItem = (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex || !items) return;
    const ordered = [...items];
    const [moved] = ordered.splice(dragIndex, 1);
    ordered.splice(targetIndex, 0, moved);
    setItems(ordered);
    setDragIndex(null);
    persistOrder(ordered);
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm({
      caption_fr: item.caption_fr,
      caption_en: item.caption_en,
      section: item.section || "realisations",
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/gallery/${editId}`, editForm);
      toast.success(t("admin_saved"));
      setEditId(null);
      load();
    } catch {
      toast.error(t("admin_save_error"));
    }
  };

  const [media, setMedia] = useState({});
  const [mediaSaving, setMediaSaving] = useState({});
  const [mediaUploading, setMediaUploading] = useState({});

  useEffect(() => {
    api.get("/content").then((r) => setMedia(r.data.items || {})).catch(() => {});
  }, []);

  const mediaText = (key, v) => setMedia((m) => ({ ...m, [key]: { fr: v, en: v } }));

  const mediaSave = async (key) => {
    setMediaSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put(`/content/${key}`, media[key] || { fr: "", en: "" });
      toast.success(t("admin_saved"));
    } catch {
      toast.error(t("admin_save_error"));
    } finally {
      setMediaSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const mediaUpload = async (key, file) => {
    setMediaUploading((s) => ({ ...s, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      const abs = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      await api.put(`/content/${key}`, { fr: abs, en: abs });
      setMedia((m) => ({ ...m, [key]: { fr: abs, en: abs } }));
      toast.success(t("admin_saved"));
    } catch {
      toast.error(t("admin_save_error"));
    } finally {
      setMediaUploading((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div data-testid="admin-gallery-panel" className="flex flex-col gap-12">
      <section data-testid="admin-slots-section" className="flex flex-col gap-5">
        <div>
          <h2 className="font-display font-semibold text-2xl flex items-center gap-3">
            <span className="w-6 h-px bg-[var(--accent)]" />
            {t("admin_slots_title")}
          </h2>
          <p className="mt-2 text-xs text-white/40 max-w-2xl leading-relaxed">{t("admin_slots_hint")}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {MEDIA_FIELDS.map((mf) => (
            <MediaField
              key={mf.key}
              label={mf.label}
              fieldKey={mf.key}
              value={media[mf.key]?.fr ?? ""}
              saving={!!mediaSaving[mf.key]}
              uploading={!!mediaUploading[mf.key]}
              onText={(v) => mediaText(mf.key, v)}
              onFile={(file) => mediaUpload(mf.key, file)}
              onSave={() => mediaSave(mf.key)}
              t={t}
              hint={mf.hintKey ? t(mf.hintKey) : null}
            />
          ))}
        </div>
      </section>

      <div className="border border-white/10 bg-white/[0.02] p-6">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
          {t("admin_gallery_add")}
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <input
            data-testid="admin-gallery-caption-fr"
            value={captionFr}
            onChange={(e) => setCaptionFr(e.target.value)}
            placeholder={t("admin_caption_fr")}
            maxLength={300}
            className={inputCls}
          />
          <input
            data-testid="admin-gallery-caption-en"
            value={captionEn}
            onChange={(e) => setCaptionEn(e.target.value)}
            placeholder={t("admin_caption_en")}
            maxLength={300}
            className={inputCls}
          />
        </div>
        <label className="block text-xs text-white/50 mb-1">{t("admin_media_section")}</label>
        <select
          data-testid="admin-gallery-section"
          value={section}
          onChange={(e) => setSection(e.target.value)}
          className={`${inputCls} mb-4`}
        >
          {MEDIA_SECTIONS.map((s) => (
            <option key={s.id} value={s.id}>
              {lang === "fr" ? s.fr : s.en}
            </option>
          ))}
        </select>
        <label
          data-testid="admin-gallery-upload"
          className="inline-flex items-center gap-2 text-xs bg-[var(--accent)] hover:bg-[var(--accent-soft)] text-white font-semibold px-5 py-3 rounded-full cursor-pointer transition-colors duration-300"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
          {busy ? t("admin_uploading") : t("admin_upload")}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
            className="hidden"
            data-testid="admin-gallery-file"
            disabled={busy}
            onChange={(e) => {
              if (e.target.files[0]) addFile(e.target.files[0]);
              e.target.value = "";
            }}
          />
        </label>
      </div>

      {items === null ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-[var(--accent)]" size={26} />
        </div>
      ) : items.length === 0 ? (
        <p data-testid="admin-gallery-empty" className="text-white/45 py-12 text-center font-display italic text-xl">
          {t("admin_gallery_empty")}
        </p>
      ) : (
        <>
          <p className="text-xs text-white/40">{t("admin_gallery_reorder_hint")}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((item, i) => (
              <div key={item.id}>
                <figure
                  data-testid={`admin-gallery-item-${item.id}`}
                  draggable
                  onDragStart={() => setDragIndex(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropItem(i)}
                  className={`relative group border overflow-hidden aspect-[4/3] bg-[#172A45] cursor-grab active:cursor-grabbing transition-all duration-300 ${
                    dragIndex === i ? "opacity-40 border-[var(--accent)]" : "border-white/10"
                  }`}
                >
                  {/\.(mp4|webm|mov)(\?|$)/i.test(item.url) ? (
                    <video src={item.url} className="w-full h-full object-cover" muted preload="metadata" />
                  ) : (
                    <img src={item.url} alt={item.caption_fr} className="w-full h-full object-cover" loading="lazy" />
                  )}
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#0B132B]/85 px-2 py-1 font-mono2 text-[9px] uppercase tracking-widest text-[var(--accent)]">
                    <GripVertical size={10} />
                    {(MEDIA_SECTIONS.find((s) => s.id === (item.section || "realisations")) || {})[lang] || item.section}
                  </span>
                  {item.caption_fr && (
                    <figcaption className="absolute inset-x-0 bottom-0 bg-[#0B132B]/85 px-3 py-2 text-xs text-white/80 truncate">
                      {item.caption_fr}
                    </figcaption>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    <button
                      data-testid={`admin-gallery-edit-${item.id}`}
                      onClick={() => startEdit(item)}
                      className="w-8 h-8 grid place-items-center bg-[#0B132B]/85 hover:bg-white hover:text-[#0B132B] text-white rounded-full transition-colors duration-300"
                      aria-label={t("admin_edit")}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      data-testid={`admin-gallery-delete-${item.id}`}
                      onClick={() => remove(item)}
                      className="w-8 h-8 grid place-items-center bg-[#0B132B]/85 hover:bg-[var(--accent)] hover:text-[#0B132B] text-white rounded-full transition-colors duration-300"
                      aria-label={t("admin_delete")}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </figure>
                {editId === item.id && (
                  <div data-testid={`admin-gallery-editbox-${item.id}`} className="border border-[var(--accent)]/30 bg-white/[0.03] p-3 mt-2 flex flex-col gap-2">
                    <input
                      data-testid={`admin-gallery-edit-caption-fr-${item.id}`}
                      value={editForm.caption_fr}
                      onChange={(e) => setEditForm({ ...editForm, caption_fr: e.target.value })}
                      placeholder={t("admin_caption_fr")}
                      maxLength={300}
                      className={inputCls}
                    />
                    <input
                      data-testid={`admin-gallery-edit-caption-en-${item.id}`}
                      value={editForm.caption_en}
                      onChange={(e) => setEditForm({ ...editForm, caption_en: e.target.value })}
                      placeholder={t("admin_caption_en")}
                      maxLength={300}
                      className={inputCls}
                    />
                    <select
                      data-testid={`admin-gallery-edit-section-${item.id}`}
                      value={editForm.section}
                      onChange={(e) => setEditForm({ ...editForm, section: e.target.value })}
                      className={inputCls}
                    >
                      {MEDIA_SECTIONS.map((s) => (
                        <option key={s.id} value={s.id}>
                          {lang === "fr" ? s.fr : s.en}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <button
                        data-testid={`admin-gallery-edit-save-${item.id}`}
                        onClick={saveEdit}
                        className="inline-flex items-center gap-2 text-xs bg-[var(--accent)] hover:bg-[var(--accent-soft)] text-white font-semibold px-4 py-2.5 rounded-full transition-colors duration-300"
                      >
                        {t("admin_save")}
                      </button>
                      <button
                        data-testid={`admin-gallery-edit-cancel-${item.id}`}
                        onClick={() => setEditId(null)}
                        className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-white/40 px-4 py-2.5 rounded-full transition-colors duration-300"
                      >
                        <X size={13} />
                        {t("admin_cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
